import { codeableConceptLabel, hasMatchingCode } from '@ctw/shared/api/fhir/codeable-concept';
import {
  compactToTruthyAndZero,
  formatPeriod,
  formatQuantityWithUnits,
  formatRange,
} from '@ctw/shared/api/fhir/formatters';
import { convertQuantityToImperial } from '@ctw/shared/api/fhir/unit-conversion';
import { formatFHIRDate } from '@ctw/shared/utils/dates';
import { VITALS_LOINC_CODES } from '@ctw/shared/utils/value-sets';
import type { NormalizedUnitSystem } from '@zeus-health/types';
import type { Basic, Observation, Provenance } from 'fhir/r4';
import { getBinaryIDFromProvenance } from '../provenance';
import { SYSTEM_CPT, SYSTEM_LOINC } from '../system-urls';
import type { ResourceMap } from '../types';
import { type DiagnosticReportModel, findEnrichedLoincCoding } from './diagnostic-report';
import { FHIRModel } from './fhir-model';

const alertInterpretations = [
  'high',
  'low',
  'hi',
  'abn',
  '(high)',
  '(low)',
  'abnormal',
  'a',
  'l',
  'h',
  'above high normal',
  'crit',
  'below low normal',
  'high alert',
  '(abnormal)',
  'abnormal alert',
  'abnormal low',
  'abnormal high',
  'low alert',
  'pos',
  'no reference range',
  'positive',
  '(positive)',
  '(critical high)',
  '(critical low)',
  'critical high',
  'critical low',
  'c',
];

export class ObservationModel extends FHIRModel<Observation> {
  public kind = 'Observation' as const;

  public binaryId?: string;

  public diagnosticReport?: DiagnosticReportModel;

  public provenance?: Array<Provenance>;

  public trends?: Array<ObservationModel>;

  public isLoadingTrends = false;

  public constructor(
    resource: Observation,
    provenance?: Array<Provenance>,
    includedResources?: ResourceMap,
    basics?: Array<Basic>,
  ) {
    super(resource, includedResources, basics);
    this.provenance = provenance;
    this.binaryId = getBinaryIDFromProvenance(this.provenance);
  }

  public get loincCode() {
    return findEnrichedLoincCoding(this.resource.code.coding);
  }

  public get dateDisplay() {
    return this.date ? formatFHIRDate(this.date) : undefined;
  }

  public get date() {
    return this.resource.effectivePeriod?.start || this.resource.effectiveDateTime;
  }

  public get valueIsInNotes() {
    return this.resource.valueString?.toLowerCase() === 'see note';
  }

  private getValue(includeUnits = true, unitSystem?: NormalizedUnitSystem): string {
    if (this.resource.valueBoolean !== undefined) {
      return this.resource.valueBoolean ? 'true' : 'false';
    }

    if (this.resource.valueCodeableConcept) {
      return codeableConceptLabel(this.resource.valueCodeableConcept);
    }

    if (this.resource.valueDateTime) {
      return this.resource.valueDateTime;
    }

    if (this.resource.valueInteger !== undefined) {
      return String(this.resource.valueInteger);
    }

    if (this.resource.valuePeriod) {
      return formatPeriod(this.resource.valuePeriod) ?? '';
    }

    if (this.resource.valueQuantity) {
      const converted = this.isRecognizedVitalSign
        ? convertQuantityToImperial(this.resource.valueQuantity, unitSystem)
        : undefined;
      if (converted) {
        const { comparator } = this.resource.valueQuantity;
        return includeUnits
          ? compactToTruthyAndZero([`${comparator ?? ''}${converted.value}`, converted.unit]).join(
              ' ',
            )
          : converted.value;
      }
      return includeUnits
        ? formatQuantityWithUnits(this.resource.valueQuantity)
        : (this.resource.valueQuantity.value?.toString() ?? '');
    }

    if (this.resource.valueRange) {
      return formatRange(this.resource.valueRange);
    }

    if (this.resource.valueString) {
      if ((this.valueIsInNotes || (this.resource.valueString ?? '') === '') && this.notes) {
        return this.notes;
      }
      return this.resource.valueString;
    }

    if (this.resource.valueTime) {
      return this.resource.valueTime;
    }

    return '';
  }

  public get valueWithUnits() {
    return this.getValue(true);
  }

  public get valueWithoutUnits() {
    return this.getValue(false);
  }

  public get valueNumeric() {
    const valueNumeric = Number.parseFloat(this.valueWithoutUnits);
    if (Number.isNaN(valueNumeric)) {
      return undefined;
    }
    return valueNumeric;
  }

  public get unit() {
    return this.resource.valueQuantity?.unit;
  }

  /**
   * Like `valueWithUnits`, but converts to `unitSystem` for a recognized vital sign with a
   * convertible UCUM code.
   */
  public getValueWithUnits(unitSystem?: NormalizedUnitSystem) {
    return this.getValue(true, unitSystem);
  }

  /**
   * Like `valueWithoutUnits`, but converts to `unitSystem` for a recognized vital sign with a
   * convertible UCUM code.
   */
  public getValueWithoutUnits(unitSystem?: NormalizedUnitSystem) {
    return this.getValue(false, unitSystem);
  }

  /**
   * Like `referenceRange`, but `undefined` whenever the value converted — free text has no
   * units to convert alongside it.
   */
  public getReferenceRange(unitSystem?: NormalizedUnitSystem) {
    if (
      this.isRecognizedVitalSign &&
      convertQuantityToImperial(this.resource.valueQuantity, unitSystem)
    ) {
      return undefined;
    }
    return this.referenceRange;
  }

  /** Reports whether this Observation's own code is a recognized vital sign. */
  private get isRecognizedVitalSign() {
    return hasMatchingCode(SYSTEM_LOINC, VITALS_LOINC_CODES, this.resource.code);
  }

  public get interpretation() {
    const nonAcceptedValues = [
      'n',
      'noinformation',
      'normal',
      'na',
      '(normal)',
      'unknown',
      'not applicable',
      'temporarily unavailable',
      'nml',
      'norm',
      '*',
    ];

    const interpretation = codeableConceptLabel(this.resource.interpretation?.[0]).toLowerCase();
    if (nonAcceptedValues.includes(interpretation)) {
      return undefined;
    }

    return interpretation;
  }

  public get notes() {
    return this.resource.note?.[0].text;
  }

  public get referenceRange() {
    return this.resource.referenceRange?.[0].text;
  }

  public get interpretationStyling(): string {
    if (this.isAlertInterpretation) {
      return 'text-caution-text bg-caution-badge inline-flex rounded-full font-medium text-sm p-0.5 px-2';
    }
    return 'text-background-inverse bg-background-disabled inline-flex rounded-xl leading-5 font-medium text-sm p-0.5 px-2';
  }

  public get isAlertInterpretation() {
    return alertInterpretations.includes(
      codeableConceptLabel(this.resource.interpretation?.[0]).toLowerCase(),
    );
  }

  /**
   * Return true if the provided diagnostic and trend combination are incorrectly coded,
   * and therefore should not be displayed in the trends.
   *
   * https://zeushealth.atlassian.net/browse/CDEV-310
   */
  public get isIncorrectlyCodedGlucose() {
    if (this.diagnosticReport) {
      const diagnosticFlagged = this.diagnosticReport.resource.code.coding?.some((coding) => {
        const a1cDisplay = coding.display?.toLowerCase().indexOf('a1c');
        return (
          (coding.system === SYSTEM_LOINC && coding.code === '4548-4') ||
          (coding.system === SYSTEM_CPT && coding.code === '83036') ||
          (a1cDisplay !== undefined && a1cDisplay > -1)
        );
      });
      const trendFlagged = this.resource.code.coding?.some(
        (coding) => coding.system === SYSTEM_LOINC && coding.code === '2345-7',
      );
      return diagnosticFlagged && trendFlagged;
    }
    return false;
  }

  public get title() {
    return this.loincCode?.display || codeableConceptLabel(this.resource.code);
  }
}
